import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Space, Tag, Modal, message, Drawer, Form, Input, InputNumber, Switch, Table, Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  listPipelines, deletePipeline,
  listStages, createStage, updateStage, deleteStage,
  type Pipeline, type PipelineStage,
  type CreatePipelineStageRequest, type UpdatePipelineStageRequest,
} from '@/api/pipelineApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  inactive: 'default',
};

export default function PipelineListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const canWrite = hasPermission('pipelines', 'write');
  const canDelete = hasPermission('pipelines', 'delete');

  // --- Stage Drawer State ---
  const [stageDrawerOpen, setStageDrawerOpen] = useState(false);
  const [activePipeline, setActivePipeline] = useState<Pipeline | null>(null);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [stageFormOpen, setStageFormOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<PipelineStage | null>(null);
  const [stageForm] = Form.useForm();
  const [stageSubmitting, setStageSubmitting] = useState(false);

  const fetchData = useCallback(
    (params: SearchParams) => listPipelines(params),
    []
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('pipelines.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deletePipeline(id);
            message.success(t('pipelines.delete_success'));
            gridRef.current?.reload();
          } catch (err) {
            const msg = err instanceof Error ? err.message : t('app.error');
            message.error(msg);
          }
        },
      });
    },
    [t]
  );

  const loadStages = useCallback(async (pipelineId: string) => {
    setStagesLoading(true);
    try {
      const res = await listStages(pipelineId);
      setStages(res.items);
    } catch {
      setStages([]);
    } finally {
      setStagesLoading(false);
    }
  }, []);

  const openStageDrawer = useCallback(
    (pipeline: Pipeline) => {
      setActivePipeline(pipeline);
      setStageDrawerOpen(true);
      loadStages(pipeline.id);
    },
    [loadStages]
  );

  const handleStageSubmit = useCallback(
    async (values: {
      name: string;
      sort_order?: number;
      probability?: number;
      is_won?: boolean;
      is_lost?: boolean;
    }) => {
      if (!activePipeline) return;
      setStageSubmitting(true);
      try {
        if (editingStage) {
          const data: UpdatePipelineStageRequest = {
            name: values.name,
            sort_order: values.sort_order ?? 0,
            probability: values.probability ?? 0,
            is_won: values.is_won ?? false,
            is_lost: values.is_lost ?? false,
          };
          await updateStage(editingStage.id, data);
          message.success(t('pipelines.stage_update_success'));
        } else {
          const data: CreatePipelineStageRequest = {
            name: values.name,
            pipeline_id: activePipeline.id,
            sort_order: values.sort_order ?? 0,
            probability: values.probability ?? 0,
            is_won: values.is_won ?? false,
            is_lost: values.is_lost ?? false,
          };
          await createStage(data);
          message.success(t('pipelines.stage_create_success'));
        }
        setStageFormOpen(false);
        setEditingStage(null);
        stageForm.resetFields();
        loadStages(activePipeline.id);
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('app.error');
        message.error(msg);
      } finally {
        setStageSubmitting(false);
      }
    },
    [activePipeline, editingStage, stageForm, t, loadStages]
  );

  const handleStageDelete = useCallback(
    async (stageId: string) => {
      if (!activePipeline) return;
      try {
        await deleteStage(stageId);
        message.success(t('pipelines.stage_delete_success'));
        loadStages(activePipeline.id);
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('app.error');
        message.error(msg);
      }
    },
    [activePipeline, t, loadStages]
  );

  const openEditStage = useCallback(
    (stage: PipelineStage) => {
      setEditingStage(stage);
      stageForm.setFieldsValue({
        name: stage.name,
        sort_order: stage.sort_order,
        probability: stage.probability,
        is_won: stage.is_won,
        is_lost: stage.is_lost,
      });
      setStageFormOpen(true);
    },
    [stageForm]
  );

  const openAddStage = useCallback(() => {
    setEditingStage(null);
    stageForm.resetFields();
    setStageFormOpen(true);
  }, [stageForm]);

  // --- Columns ---

  const columns: ColumnsType<Pipeline> = useMemo(
    () => [
      {
        title: t('pipelines.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
      },
      {
        title: t('pipelines.field.description'),
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
      },
      {
        title: t('pipelines.field.is_default'),
        dataIndex: 'is_default',
        key: 'is_default',
        width: 100,
        render: (val: boolean) =>
          val ? <Tag color="blue">{t('app.yes')}</Tag> : <Tag>{t('app.no')}</Tag>,
      },
      {
        title: t('pipelines.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`pipelines.status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('pipelines.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 180,
        render: (_: unknown, record: Pipeline) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => openStageDrawer(record)}
            >
              {t('pipelines.stages')}
            </Button>
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/pipelines/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, openStageDrawer]
  );

  const stageColumns: ColumnsType<PipelineStage> = useMemo(
    () => [
      {
        title: t('pipelines.stage_field.name'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t('pipelines.stage_field.sort_order'),
        dataIndex: 'sort_order',
        key: 'sort_order',
        width: 80,
      },
      {
        title: t('pipelines.stage_field.probability'),
        dataIndex: 'probability',
        key: 'probability',
        width: 80,
        render: (val: number) => `${val}%`,
      },
      {
        title: t('pipelines.stage_field.is_won'),
        dataIndex: 'is_won',
        key: 'is_won',
        width: 70,
        render: (val: boolean) =>
          val ? <Tag color="green">{t('app.yes')}</Tag> : null,
      },
      {
        title: t('pipelines.stage_field.is_lost'),
        dataIndex: 'is_lost',
        key: 'is_lost',
        width: 70,
        render: (val: boolean) =>
          val ? <Tag color="red">{t('app.yes')}</Tag> : null,
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 100,
        render: (_: unknown, record: PipelineStage) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditStage(record)}
              />
            )}
            {canDelete && (
              <Popconfirm
                title={t('pipelines.stage_confirm_delete')}
                onConfirm={() => handleStageDelete(record.id)}
                okText={t('app.yes')}
                cancelText={t('app.no')}
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, canDelete, openEditStage, handleStageDelete]
  );

  const toolbar = (
    <Space>
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/pipelines/new')}
        >
          {t('pipelines.new_pipeline')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('pipelines.list_title')}</h2>
      <DataGrid<Pipeline>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('pipelines.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />

      {/* Stage Management Drawer */}
      <Drawer
        title={activePipeline ? `${activePipeline.name} — ${t('pipelines.stages')}` : ''}
        open={stageDrawerOpen}
        onClose={() => {
          setStageDrawerOpen(false);
          setStageFormOpen(false);
          setEditingStage(null);
        }}
        width={600}
      >
        <Space style={{ marginBottom: 16 }}>
          {canWrite && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAddStage}>
              {t('pipelines.new_stage')}
            </Button>
          )}
        </Space>

        <Table<PipelineStage>
          columns={stageColumns}
          dataSource={stages}
          rowKey="id"
          loading={stagesLoading}
          pagination={false}
          size="small"
        />

        {/* Stage Form Modal */}
        <Modal
          title={editingStage ? t('pipelines.edit_stage') : t('pipelines.new_stage')}
          open={stageFormOpen}
          onCancel={() => {
            setStageFormOpen(false);
            setEditingStage(null);
            stageForm.resetFields();
          }}
          onOk={() => stageForm.submit()}
          confirmLoading={stageSubmitting}
        >
          <Form
            form={stageForm}
            layout="vertical"
            onFinish={handleStageSubmit}
            initialValues={{ sort_order: 0, probability: 0, is_won: false, is_lost: false }}
          >
            <Form.Item
              name="name"
              label={t('pipelines.stage_field.name')}
              rules={[
                { required: true, message: t('pipelines.validation.stage_name_required') },
                { max: 200, message: t('pipelines.validation.name_max_length') },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="sort_order" label={t('pipelines.stage_field.sort_order')}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="probability" label={t('pipelines.stage_field.probability')}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="is_won" label={t('pipelines.stage_field.is_won')} valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="is_lost" label={t('pipelines.stage_field.is_lost')} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      </Drawer>
    </div>
  );
}
